import React, {useEffect, useRef, useState} from 'react';
import TableSkeleton from "../../Loader/Loader";
import ErrorPresentor from "../../ErrorPresentor/ErrorPresentor";
import {Card} from "primereact/card";
import useApi from "../../hooks/useApi";
import {webInfo} from "../../utils/webInfo";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {Paginator} from "primereact/paginator";
import {Dialog} from "primereact/dialog";
import useDelete from "../../hooks/useDelete";
import callToast, {catchError} from "../../utils/helper";
import {Toast} from "primereact/toast";
import {useNavigate} from "react-router-dom";
import EmptyData from "../../components/EmptyData";

interface Company {
    id: number;
    name: string;
    description: string;
    slug: string;
    created_at: string;
    updated_at: string;
}


const Companies = () => {
    const navigate = useNavigate();
    const {get, loading, error} = useApi<any>();
    const {remove, loading: deleteLoading} = useDelete();
    const toast = useRef<Toast | null>(null);

    const [companies, setCompanies] = useState<Company[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage] = useState(10);
    const [update, setUpdate] = useState(false);

    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [visibleConfirmation, setVisibleConfirmation] = useState(false);
    const url = `${webInfo.baseUrl}/api/admin/companies?page=${page}&per_page=${perPage}`;

    const onPageChange = (event: any) => {
        setPage(event.page + 1);
    };

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const res: any = await get(url);

                const responseData = res?.data ?? res;
                const companyList = Array.isArray(responseData)
                    ? responseData
                    : responseData?.data ?? [];

                setCompanies(companyList);
                setTotal(
                    Array.isArray(responseData)
                        ? companyList.length
                        : responseData?.total ?? companyList.length
                );
            } catch (err) {
                console.error("Failed to fetch companies", err);
            }
        };

        fetchCompanies();

        // eslint-disable-next-line
    }, [page, perPage, update]);

    const openDeleteDialog = (company: Company) => {
        setSelectedCompany(company);
        setVisibleConfirmation(true);
    };

    const closeDeleteDialog = () => {
        setVisibleConfirmation(false);
        setSelectedCompany(null);
    };

    const deleteCompany = async () => {
        if (!selectedCompany) return;
        const deleteUrl = `${webInfo.baseUrl}/api/admin/companies/${selectedCompany.id}`;
        try {
            const res: any = await remove(deleteUrl);
            if (res?.success === false) {
                callToast(toast, false, res?.error?.message ?? "Failed to delete company");
                return;
            }
            callToast(toast, true, "Company deleted successfully");
            closeDeleteDialog();
            setUpdate(!update);
        } catch (e) {
            catchError(e);
            callToast(toast, false, "Failed to delete company");
        }
    };

    const descriptionTemplate = (rowData: Company) => (
        <span>{rowData.description || "-"}</span>
    );

    const actionTemplate = (rowData: Company) => (
        <div className="flex gap-2">
            <Button
                title={'Delete'}
                icon="pi pi-trash"
                className="p-button-sm p-button-danger"
                loading={deleteLoading && selectedCompany?.id === rowData.id}
                onClick={() => openDeleteDialog(rowData)}
            />
        </div>
    );
    const deleteFooterContent = (
        <div className="flex justify-content-end align-items-center">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={closeDeleteDialog}
            />

            <Button
                label="Delete"
                severity="danger"
                className="ml-2"
                icon="pi pi-trash"
                loading={deleteLoading}
                onClick={deleteCompany}
            />
        </div>
    );

    const nameTemplate = (rowData: Company) => {
        return <span className={'underline cursor-pointer'} onClick={() => navigate(`/companies/${rowData.id}`)}>{rowData?.name}</span>
    }

    const dataTable = (
        <div>
            <Dialog
                visible={visibleConfirmation}
                modal
                header="Remove company confirmation"
                footer={deleteFooterContent}
                style={{width: '30rem'}}
                onHide={closeDeleteDialog}
            >
                <p className="m-0">
                    Are you sure you want to delete this company? Once deleted, it cannot be viewed again.
                </p>
            </Dialog>

            {(companies?.length > 0 && !loading) ? <div>
                    <DataTable
                        value={companies}
                        rows={perPage}
                        stripedRows
                        emptyMessage="No companies found"
                    >
                        <Column
                            body={nameTemplate}
                            header="Company Name"
                            sortable
                        />
                        <Column
                            field="description"
                            header="Description"
                            body={descriptionTemplate}
                        />
                        <Column
                            header="Actions"
                            body={actionTemplate}
                            style={{width: "320px", textAlign: "center"}}
                        />
                    </DataTable>
                    <Paginator
                        first={(page - 1) * perPage}
                        rows={perPage}
                        totalRecords={total}
                        rowsPerPageOptions={[10, 20, 30]}
                        onPageChange={onPageChange}
                    />
                </div> :
                <EmptyData/>}

        </div>
    );

    const header = (
        <div className="flex align-items-center justify-content-between p-4 pb-0">
            <h4 className="m-0">Companies</h4>

            <div>
                <Button
                    onClick={() => navigate('/companies/create')}
                    label="Add Company"
                    icon="pi pi-plus"
                    iconPos="right"
                />
            </div>
        </div>
    );

    return (
        <Card header={header}>
            <Toast ref={toast}/>

            {loading ? (
                <TableSkeleton/>
            ) : (
                <div>
                    {error ? <ErrorPresentor error={error}/> : dataTable}
                </div>
            )}
        </Card>
    );
};

export default Companies;
